<script setup lang="ts">
import { T, useTranslate } from '@tolgee/vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import { computed, ref } from 'vue'
import CountdownTag from '@/components/CountdownTag.vue'
import SparkleImage from '@/partners/lingo/lingo-giveaway/assets/images/prize_sparkle.png'
import { PrizeKeys, prizes, prizeSchedule } from '@/partners/lingo/lingo-giveaway/config/prizes'
import BaseButton from '@/components/base/BaseButton.vue'
import LingoGiveawayWinnersModal from '@/partners/lingo/lingo-giveaway/components/modals/LingoGiveawayWinnersModal.vue'
import { useLingoGiveaway } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'
import { twMerge } from 'tailwind-merge'

const { t } = useTranslate()

const { todayEntriesCount, raffleId, raffleWinners, giveawayCountdownSeconds } = useLingoGiveaway()

const oneDayInSeconds = 3600 * 24
const todaysPrize = computed<PrizeKeys>(() => prizeSchedule[raffleId.value])
const hasWinners = computed<boolean>(() => !!Object.keys(raffleWinners.value).length)

const showWinnersModal = ref<boolean>(false)

</script>
<template>
  <div class="flex flex-col">
    <div
      :class="twMerge('pt-4 pb-6 px-3 flex flex-col gap-y-6 rounded-t-xl border-2 border-[#FFCB00] gold-background shadow-[0px_0px_38.6px_0px_rgba(230,185,12,0.45)] overflow-hidden',
                      !hasWinners && 'rounded-b-xl')"
    >
      <div class="flex flex-col w-full items-center gap-y-3">
        <h2 class="text-center text-white font-bold text-2xl">
          <T
            key-name="lingo.giveaway.draw.title"
            default-value="Daily prize draw"
          />
        </h2>
        <div class="grid grid-cols-2 justify-center w-full">
          <div class="flex flex-col items-center gap-y-1">
            <div class="flex items-center gap-x-1 text-white/[87%]">
              <span class="font-semibold text-base max-[360px]:text-sm">
                <T
                  key-name="lingo.giveaway.draw.entries.title"
                  default-value="My ticket entries"
                />
              </span>
              <InfoCircleIcon
                v-tooltip="{content: t('lingo.giveaway.draw.entries.tooltip', 'Your raffle tickets reset to zero after each lottery draw')}"
              />
            </div>
            <div class="flex justify-center items-center px-6 py-[14px] h-12 w-[134px] rounded-[30px] bg-white/[12%]">
              <span
                v-if="todayEntriesCount"
                class="text-2xl text-white font-medium"
              >{{ todayEntriesCount }}</span>
              <div
                v-else
                class="flex items-center justify-center w-full"
              >
                <div class="h-0.5 w-[20px] bg-white " />
              </div>
            </div>
          </div>
          <div class="flex flex-col items-center gap-y-1 w-full">
            <span class="font-semibold text-base max-[360px]:text-sm text-white/[87%] text-center">
              <T
                key-name="lingo.giveaway.draw.countdown.title"
                default-value="Next prize draw"
              />
            </span>
            <CountdownTag
              :remaining-time="giveawayCountdownSeconds"
              :cooldown="oneDayInSeconds"
            />
          </div>
        </div>
        <div class="flex flex-col gap-y-3 w-full items-center">
          <div class="flex items-center justify-between w-full min-[376px]:px-2">
            <p class="text-2xl leading-7 max-[360px]:text-lg font-bold text-white/[87%]">
              <T
                key-name="lingo.giveaway.draw.prizes"
                default-value="Prizes"
              />
            </p>
            <p class="text-end max-w-[200px]">
              <span class="text-white/60 font-medium text-sm leading-5">
                <T
                  key-name="lingo.giveaway.draw.today_prize"
                  default-value="Today’s Prize:"
                />
              </span> <span class="text-sm font-bold text-white/[87%]">{{ prizes[todaysPrize].name }}</span>
            </p>
          </div>
          <div class="relative h-[96px] w-[calc(100%+40px)] overflow-hidden">
            <div class="absolute inset-0">
              <div class="flex w-max animate-prizeticker gap-6 pr-6">
                <template
                  v-for="(_,i) in Array.from({length: 3})"
                  :key="i"
                >
                  <div
                    v-for="(prizeKey, index) in Object.keys(prizes)"
                    :key="index"
                    class="relative size-24 overflow-hidden rounded-xl"
                  >
                    <div
                      v-if="todaysPrize === prizeKey"
                      class="absolute w-full h-full rounded-xl bg-gradient-to-b from-[#FEE06C] to-[#FBD753] opacity-80"
                    />
                    <div
                      v-if="todaysPrize === prizeKey"
                      class="absolute rounded-xl"
                    >
                      <img
                        class="mix-blend-plus-lighter"
                        :src="SparkleImage"
                      >
                    </div>

                    <img
                      class="relative z-10 size-full"
                      :src="prizes[prizeKey as PrizeKeys].src"
                      :alt="prizes[prizeKey as PrizeKeys].name"
                    >
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="hasWinners"
      class="w-full flex items-center justify-center rounded-b-xl overflow-hidden border-2 border-t-0 border-[#FFCB00]"
    >
      <BaseButton
        style-type="gold"
        class="w-full"
        button-class="rounded-none py-3 h-[52px]"
        @click="showWinnersModal = true"
      >
        <T
          key-name="lingo.giveaway.draw.check_win"
          default-value="Check if you won"
        />
      </BaseButton>
    </div>
  </div>
  <LingoGiveawayWinnersModal
    :show="showWinnersModal"
    @hide="showWinnersModal = false"
  />
</template>
<style scoped>
.gold-background {
  background: linear-gradient(274deg, rgba(204, 158, 54, 0.68) -15.81%, rgba(255, 255, 159, 0.68) 101.07%, rgba(204, 161, 59, 0.68) 138.61%);
}

.animate-prizeticker {
  animation: prizeticker 16s linear infinite;
}

@keyframes prizeticker {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-33.333%);
  }
}
</style>